/**
 * @file useSiteHistory.ts
 * @description История публикаций вида сайта (phase19.16 Р1; SB52, SB63,
 * SB87; контракт §6): страницы по 20 записей, «Показать ещё», перечитывание
 * после возврата версии.
 */

import { useCallback, useEffect, useState } from "react";

import { siteView } from "../../lib/api-site-view";
import { siteViewErrorText } from "../../lib/site-view/errors";
import type { SiteHistoryItem } from "../../lib/site-view/types";

export interface SiteHistoryApi {
  items: SiteHistoryItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  retentionMonths: number;
  loadMore: () => void;
  reload: () => void;
}

export function useSiteHistory(): SiteHistoryApi {
  const [items, setItems] = useState<SiteHistoryItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retentionMonths, setRetention] = useState(12);

  const load = useCallback(async (from: string | null) => {
    setLoading(true);
    try {
      const page = await siteView.history(from);
      setItems((prev) => (from ? [...prev, ...page.items] : page.items));
      setCursor(page.nextCursor);
      setRetention(page.retentionMonths);
      setError(null);
    } catch (err) {
      setError(siteViewErrorText(err, "Не удалось загрузить историю. Сайт работает как прежде."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(null);
  }, [load]);

  return {
    items,
    loading,
    error,
    hasMore: cursor !== null,
    retentionMonths,
    loadMore: () => void load(cursor),
    reload: () => void load(null),
  };
}
